import React, { useMemo, useState } from 'react';
import { X, AlertCircle, Calendar } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { formatMediaDateRange } from '../../lib/media';
import DateInput from '../ui/DateInput';

// School-side reschedule modal (spec §7).
//
// Behavior gates that mirror the backend:
//   - new start must be in the future + ≤4h window
//   - if new tier carries a fee, must check acknowledgment box
//   - one pending request per event (backend returns 409 duplicate_request)
//
// On success: parent reloads so the event card shows the "Reschedule
// pending" pill until Ops/shooter decides.

function tierFor(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return null;
  const localKey = (x) => x.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const days = Math.round((new Date(localKey(d)) - new Date(localKey(new Date()))) / 86_400_000);
  if (days >= 14) return 'standard';
  if (days >= 7) return 'rush';
  return 'emergency';
}

export default function RescheduleEventDialog({ event, onClose, onSubmitted, submitPath, showFee = true }) {
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const tier = useMemo(() => tierFor(newStart), [newStart]);
  const shortNotice = tier && tier !== 'standard';
  const disabled = submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await api.post(submitPath || `/api/client/media/events/${event.id}/reschedule`, {
        newScheduledStartAt: newStart,
        newScheduledEndAt: newEnd,
        reason,
      });
      onSubmitted?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit request');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Request reschedule</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={event.eventTitle}>
              {event.eventTitle}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-[12px] text-gray-400 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            Currently scheduled: {formatMediaDateRange(event.scheduledStartAt, event.scheduledEndAt)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">New start</span>
              <DateInput withTime required value={newStart} onChange={setNewStart}
                className={inputCls} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">New end</span>
              <DateInput withTime required value={newEnd} onChange={setNewEnd}
                className={inputCls} />
            </label>
          </div>

          {shortNotice && (
            <div className="rounded-lg border border-amber-400/30 bg-amber-500/[0.04] px-3 py-2.5 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
              <div className="text-[12px] text-amber-100/90">
                The new date is on short notice. NexSav will review availability before approving.
              </div>
            </div>
          )}

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Reason (optional)</span>
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you requesting this change?" className={inputCls} />
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={disabled}
              className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40';
